import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Clock, XCircle, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom hook for subscription status
export function useSubscriptionStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/trial-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const refetch = fetchSubscriptionStatus;

  return { status, loading, refetch };
}

export default function SubscriptionBanner({ usage }) {
  const navigate = useNavigate();
  const { status, loading } = useSubscriptionStatus();

  const handleRenew = () => {
    // Navigate to pricing page with renewal flag
    navigate(`/advisor/pricing?renew=true&plan=${status.plan_tier}`);
  };

  if (loading || !status) return null;

  // Check if approaching limits
  const approachingLimits = (usage?.client_usage_percent || 0) >= 80 || (usage?.plan_usage_percent || 0) >= 80;
  const clientLimitWarning = (usage?.client_usage_percent || 0) >= 80;
  const planLimitWarning = (usage?.plan_usage_percent || 0) >= 80;

  // Active subscription expiring soon (within 7 days)
  if (status.subscription_status === 'active' && status.show_renewal_banner) {
    return (
      <Card className="p-4 mb-6 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-300 dark:border-orange-700">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-1" />
            <div>
              <p className="text-orange-900 dark:text-orange-200 font-bold text-lg">
                ⚠️ Your subscription expires in {status.days_until_expiry} day{status.days_until_expiry !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                Renew now to continue using your {status.plan_tier?.charAt(0).toUpperCase() + status.plan_tier?.slice(1)} plan without interruption.
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                Expires on: {new Date(status.subscription_end).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <Button 
            className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg"
            onClick={handleRenew}
          >
            Renew Now
          </Button>
        </div>
      </Card>
    );
  }

  // Expired but in grace period (read-only mode)
  if (status.subscription_status === 'expired_grace' && status.in_grace_period) {
    return (
      <Card className="p-4 mb-6 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border-red-300 dark:border-red-700">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-1" />
            <div>
              <p className="text-red-900 dark:text-red-200 font-bold text-lg">
                ❌ Your subscription has expired
              </p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {status.subscription_end && (
                  <span className="font-semibold">
                    Expired on {new Date(status.subscription_end).toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                )}
                {' • '}You're in a {status.grace_days_left}-day grace period. You can view existing data but cannot create new clients or plans.
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-semibold">
                ⏰ Grace period ends in {status.grace_days_left} day{status.grace_days_left !== 1 ? 's' : ''} - After that, your account will be suspended.
              </p>
            </div>
          </div>
          <Button 
            className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
            onClick={handleRenew}
          >
            Renew to Continue
          </Button>
        </div>
      </Card>
    );
  }

  // Fully blocked (grace period ended)
  if (status.subscription_status === 'expired_blocked' || status.is_blocked) {
    return (
      <Card className="p-4 mb-6 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border-gray-400 dark:border-gray-600">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-gray-700 dark:text-gray-300 flex-shrink-0 mt-1" />
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-bold text-lg">
                🚫 Account Suspended
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                Your grace period has ended. Renew your subscription to restore full access to your account.
              </p>
            </div>
          </div>
          <Button 
            className="bg-gray-700 hover:bg-gray-800 text-white shadow-lg"
            onClick={handleRenew}
          >
            Renew Now
          </Button>
        </div>
      </Card>
    );
  }

  // Active subscription (not expiring soon)
  if (status.subscription_status === 'active') {
    // If approaching limits on active subscription, show upgrade prompt
    if (approachingLimits) {
      return (
        <Card className="p-4 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-amber-900 dark:text-amber-200 font-semibold text-lg">
                    Approaching Limits
                  </span>
                  <span className="px-2.5 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-100 text-sm font-medium rounded-full">
                    {status.plan_tier?.charAt(0).toUpperCase() + status.plan_tier?.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {clientLimitWarning && `${usage?.clients_remaining || 0} client slots remaining. `}
                  {planLimitWarning && `${usage?.plans_remaining || 0} plans remaining. `}
                  <span className="text-amber-600 dark:text-amber-400">Upgrade to continue growing.</span>
                </p>
              </div>
            </div>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => navigate('/advisor/pricing')}
            >
              Upgrade Plan
            </Button>
          </div>
        </Card>
      );
    }
    
    // Regular active subscription banner
    return (
      <Card className="p-4 mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-emerald-900 dark:text-emerald-200 font-semibold text-lg">
                  {status.plan_tier?.charAt(0).toUpperCase() + status.plan_tier?.slice(1)} Plan
                </span>
                <span className="px-2.5 py-0.5 bg-emerald-200 dark:bg-emerald-700 text-emerald-800 dark:text-emerald-100 text-sm font-medium rounded-full">
                  Active
                </span>
              </div>
              <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                Renews on {new Date(status.subscription_end).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            onClick={() => navigate('/advisor/pricing')}
          >
            Manage Plan
          </Button>
        </div>
      </Card>
    );
  }

  // Trial active (combined with limit warning if approaching limits)
  if (status.subscription_status === 'trial' && status.days_remaining > 0) {
    // If approaching limits, show a more urgent combined message
    if (approachingLimits) {
      return (
        <Card className="p-4 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-amber-900 dark:text-amber-200 font-semibold text-lg">
                    Approaching Limits
                  </span>
                  <span className="px-2.5 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-100 text-sm font-medium rounded-full">
                    {status.days_remaining} day{status.days_remaining !== 1 ? 's' : ''} left
                  </span>
                </div>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {clientLimitWarning && `${usage?.clients_remaining || 0} client slots remaining. `}
                  {planLimitWarning && `${usage?.plans_remaining || 0} plans remaining. `}
                  <span className="text-amber-600 dark:text-amber-400">Upgrade to continue growing your practice.</span>
                </p>
              </div>
            </div>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => navigate('/advisor/pricing')}
            >
              View Plans
            </Button>
          </div>
        </Card>
      );
    }
    
    // Regular trial banner (no limits warning)
    // Calculate trial end date
    const trialEndDate = status.subscription_end 
      ? new Date(status.subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;
    
    return (
      <Card className="p-4 mb-6 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200 dark:border-violet-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-800 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-violet-900 dark:text-violet-200 font-semibold text-lg">
                  Free Trial
                </span>
                <span className="px-2.5 py-0.5 bg-violet-200 dark:bg-violet-700 text-violet-800 dark:text-violet-100 text-sm font-medium rounded-full">
                  {status.days_remaining} day{status.days_remaining !== 1 ? 's' : ''} left
                </span>
              </div>
              <p className="text-sm text-violet-700 dark:text-violet-300 mt-1">
                {trialEndDate ? `Ends on ${trialEndDate}` : 'Explore all features during your trial'}
                <span className="mx-2">•</span>
                <span className="text-violet-600 dark:text-violet-400">Upgrade anytime</span>
              </p>
            </div>
          </div>
          <Button 
            className="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => navigate('/advisor/pricing')}
          >
            View Plans
          </Button>
        </div>
      </Card>
    );
  }

  // Trial expired
  if (status.subscription_status === 'expired' || status.is_expired) {
    return (
      <Card className="p-4 mb-6 bg-gradient-to-r from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 border-rose-200 dark:border-rose-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-800 flex items-center justify-center flex-shrink-0">
              <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-rose-900 dark:text-rose-200 font-semibold text-lg">
                  Free Trial Ended
                </span>
                <span className="px-2.5 py-0.5 bg-rose-200 dark:bg-rose-700 text-rose-800 dark:text-rose-100 text-sm font-medium rounded-full">
                  Expired
                </span>
              </div>
              <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">
                {status.trial_end && (
                  <>
                    Expired on {new Date(status.trial_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    <span className="mx-2">•</span>
                  </>
                )}
                <span className="text-rose-600 dark:text-rose-400">Choose a plan to create new clients & plans</span>
              </p>
            </div>
          </div>
          <Button 
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={() => navigate('/advisor/pricing')}
          >
            Choose Plan
          </Button>
        </div>
      </Card>
    );
  }

  return null;
}
